// /Components/LoginPage.jsx
import { useState, useRef, useEffect, useContext } from "react";
import {
  Lock, Smartphone, Eye, EyeOff, ArrowRight, ShieldCheck, Sparkles
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ToastContainer from "../Utils/ToastContainer";
import { toast } from "react-hot-toast";
import "react-toastify/dist/ReactToastify.css";
import { GlobalContext } from "../Context/GlobalContext";
import anlook_logo from "../Assets/anlook_logo.png";

// ✅ Left panel background image (use as provided)
import bg from "../Assets/anlook_admin_bg.png";

import useLogin from "./Services/LoginServices/useLogin";
import useClients from "./Services/ClientServices/useClient";

const LoginPage = ({ onLogin }) => {
  const {adminLogin, }=useLogin();
  const {fetchAll}=useClients();


  const { api, adminId } = useContext(GlobalContext);

  const [form, setForm] = useState({ mobile: "", password: "" });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [location, setLocation] = useState({ lat: "", lng: "" });

  const passwordRef = useRef("");
  const otpRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) }),
        () => {}
      );
    }
  }, []);

  useEffect(() => {
    if (form.password !== undefined) {
      passwordRef.current = form.password;
      if (form.password) sessionStorage.setItem("login_pw_cache", form.password);
    }
  }, [form.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleOtpChange = (e, i) => {
    const { value } = e.target;
    if (/^[0-9]?$/.test(value)) {
      const next = [...otp];
      next[i] = value;
      setOtp(next);
      if (value && i < 5) otpRefs.current[i + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (e, i) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const isFormValid = () => {
    if (!/^[0-9]{10}$/.test(form.mobile.trim())) return toast.error("Enter a valid 10-digit mobile number."), false;
    if (step === 1 && !form.password.trim()) return toast.error("Please enter your password."), false;
    if (step === 2 && otp.join("").length !== 6) return toast.error("Please enter full 6-digit OTP."), false;
    return true;
  };

  const buildPayload = () => {
    const cachedPw = sessionStorage.getItem("login_pw_cache") || "";
    const safePassword = form.password || passwordRef.current || cachedPw;
    const base = { mobile: form.mobile, step: String(step), latitude: location.lat, longitude: location.lng, password: safePassword };
    return step === 2 ? { ...base, otp: otp.join("") } : base;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!isFormValid()) return;

  setLoading(true);

  try {
    const result =
      await adminLogin(buildPayload());

    if (result.success) {

      // STEP 1 → OTP FLOW
      if (step === 1) {
        toast.success(
          "Password verified! Enter OTP."
        );
        setStep(2);
        return;
      }

      const data = result.data;

      const adminMeta = {
        adminId: data.pkid ?? "",
        adminNo: data.adminno ?? "",
        name: data.adname ?? "",
        roleId: data.fk_role_id ?? "",
        roleName: data.rolename ?? "",
        portalId: data.portalId ?? "",
        mobile: data.mobile ?? "",
        userType: data.userType ?? "",
      };

      const token =
        data.token ||
        data.accessToken ||
        null;

      const user = {
        id:
          data.pkid ??
          data.id ??
          "",
        name:
          data.adname ??
          data.name ??
          "",
      };

      const expiresAt =
        data.expiresAt || null;

      sessionStorage.setItem(
        "userData",
        JSON.stringify({
          isAuthenticated: true,
          user,
          token,
          adminMeta,
          rawLoginResponse: data,
          loginAt: Date.now(),
          expiresAt,
        })
      );

      sessionStorage.removeItem(
        "login_pw_cache"
      );

      toast.success("Login successful!");

      onLogin?.();
      navigate("/admin/home");
      adminId && fetchAll();

    } else {
      toast.error(result.message);
    }

  } finally {
    setLoading(false);
  }
};


  return (
    <>
      <ToastContainer />

      {/* Full-screen split */}
      <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        {/* LEFT PANEL — background image with centered bigger text */}
   <section
      className="relative flex items-center justify-center text-white"
  style={{
    backgroundImage: `url("${bg}")`,   // quotes help with any special chars
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  }}
    >
      <div className="absolute inset-0 bg-black/25" />
      <div className="relative z-10 mx-auto max-w-3xl px-6 text-start select-none">
        <div className=" mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm text-bold">
          <Sparkles size={24} />
          
        </div>
        <h1 className="montserrat-font-1 text-5xl lg:text-6xl leading-tight">Hello, Anlook Admin!</h1>
        <p className="inter-font-2 mt-4 text-base opacity-95">
          Approve orders, manage campaigns, track settlements, and resolve support tickets — faster and smarter.
        </p>
        <p className="mt-8 text-xs opacity-80">
          © {new Date().getFullYear()} Anlook. All rights reserved.
        </p>
      </div>
    </section>
        {/* <img src={anlook_background} alt="test" /> */}


        {/* RIGHT PANEL — login form */}
        <section className="relative bg-white">
          <div className="flex h-full items-center">
            <div className="mx-auto w-[min(520px,90vw)] px-6 py-12">
              <div className="mb-6">
                <img src={anlook_logo} alt="Anlook" className="h-8" />
              </div>

              <h2 className="montserrat-font-1 text-[28px] font-bold text-gray-900">Welcome back!</h2>
              <p className="mt-1 text-sm text-gray-600">
                Don’t have an account? <a href="#" className="font-semibold text-[#0f766e] hover:underline">Create a new account</a>
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Mobile</label>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-[#00a97a]">
                    <Smartphone size={18} className="text-gray-400" />
                    <input
                      type="text"
                      name="mobile"
                      value={form.mobile}
                      onChange={handleChange}
                      placeholder="Enter 10-digit number"
                      className="flex-1 bg-transparent outline-none text-sm"
                      autoComplete="tel"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                {step === 1 && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Password</label>
                    <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-[#00a97a]">
                      <Lock size={18} className="text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Your password"
                        className="flex-1 bg-transparent outline-none text-sm"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-700">Enter 6-digit OTP</label>
                    <div className="flex gap-2">
                      {otp.map((d, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          maxLength="1"
                          value={d}
                          onChange={(e) => handleOtpChange(e, i)}
                          onKeyDown={(e) => handleOtpKeyDown(e, i)}
                          className="h-12 w-11 rounded-xl border border-gray-300 bg-white text-center text-base font-semibold outline-none focus:ring-2 focus:ring-[#00a97a]"
                          inputMode="numeric"
                        />
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">We’ve sent a verification code to your registered mobile number.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0f766e] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:brightness-[1.08] transition"
                >
                  {loading ? "Processing…" : step === 2 ? "Verify & Login" : "Login Now"}
                  <ArrowRight size={16} className="opacity-90 group-hover:translate-x-0.5 transition" />
                </button>

                <div className="text-center text-sm">
                  <a href="#" className="font-medium text-[#0f766e] hover:underline">Forgot password?</a>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LoginPage;
